/**
 * Types shared by the plain-JSON component specs (GridSpec, ChartSpec): the field catalog a host
 * supplies, the serializable format descriptors, and the result of applying ops.
 *
 * Everything here must stay JSON-serializable - no functions. Formats become code only in Formats.ts.
 */

//------------------------
// Field catalog - supplied by the host, describes the data available to the component
//------------------------
export type FieldDataType = 'string' | 'number' | 'date' | 'bool';

export interface FieldCatalogEntry {
    name: string;
    label?: string;
    type: FieldDataType;
    /** Free-text unit hint for the AI and the format dialog, e.g. 'USD', 'USD millions', '%'. */
    unit?: string;
    description?: string;
    /** True if the field can be used to group rows. */
    isDimension?: boolean;
}

export type FieldCatalog = FieldCatalogEntry[];

//------------------------
// Formats
//------------------------
export type FormatType = 'text' | 'number' | 'date' | 'bool';
export type NumberStyle = 'plain' | 'thousands' | 'millions' | 'billions' | 'percent' | 'price' | 'quantity';
export type DateStyle = 'date' | 'dateTime' | 'dateTimeSec' | 'time';

export const NUMBER_STYLES: NumberStyle[] = ['plain', 'thousands', 'millions', 'billions', 'percent', 'price', 'quantity'];
export const DATE_STYLES: DateStyle[] = ['date', 'dateTime', 'dateTimeSec', 'time'];

export interface FormatSpec {
    type: FormatType;

    // number
    numberStyle?: NumberStyle;
    /** Decimal places, or 'auto'. */
    precision?: number | 'auto';
    prefix?: string;
    /** Label appended to the value, e.g. 'm' for millions. */
    suffix?: string;
    /** Shortcut for prefix '$' (ignored for percent). */
    currency?: boolean;
    /** Color positive values green and negative values red. */
    colorSpec?: boolean;
    /** Show negative values in parentheses. */
    ledger?: boolean;
    zeroPad?: boolean;
    withPlusSign?: boolean;

    // date
    dateStyle?: DateStyle;
    /** Moment.js format string overriding dateStyle, e.g. 'YYYY' or 'MMM YYYY'. */
    dateFmt?: string;
}

//------------------------
// Ops
//------------------------
export interface RejectedSpecOp<O> {
    op: O;
    reason: string;
}

export interface ApplySpecOpsResult<S, O> {
    spec: S;
    applied: O[];
    rejected: RejectedSpecOp<O>[];
    /** What applied ops left out - e.g. a field a widget's dataset doesn't have. */
    warnings?: string[];
}
